import {ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, input, Signal, viewChild} from "@angular/core";
import {BeslutningId, BeslutningModell, OppgaveModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MatIcon} from "@angular/material/icon";
import {BekreftKlikkDirektiv} from "web-ng2/delt/direktiver/button--bekreftKlikk/button--bekreftKlikk";
import {MatIconButton} from "@angular/material/button";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {TranslateModule} from "@ngx-translate/core";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erFavorittHosKallendeBruker} from "felles-ng2/modell/favoritterModellHjelpere";
import {MatDialog} from "@angular/material/dialog";
import {BeslutningRedigerDialogKomponent} from "../../modale/beslutning-rediger-dialog/beslutning-rediger-dialog";
import {BeslutningDatatjeneste} from "felles-ng2/tjenester/datalager/BeslutningDatatjeneste";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {beslutningBeskrivelseEllerForslag, erMigrertBeslutning, kanBeslutningRedigeres, nullsikkerBeslutningTittelEllerForslag} from "felles-ng2/modell/beslutningModellHjelpere";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Observable, switchMap} from "rxjs";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {OppgavelisteKompaktKomponent} from "web-ng2/moduler/oppgave/komponenter/oppgaveliste-kompakt/oppgaveliste-kompakt";
import {MatDivider} from "@angular/material/divider";
import {OppgaveOpprettDialogKomponent} from "web-ng2/moduler/oppgave/modale/oppgave-opprett-dialog/oppgave-opprett-dialog";
import {erIkkeTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {scrollDersomNødvendig} from "web-ng2/delt/dom/scrollIntoView";
import {ListeLenkerelasjonerKomponent} from "web-ng2/moduler/lenkrelasjoner/komponenter/liste-lenkerelasjoner/liste-lenkerelasjoner";
import {EntitetikonKomponent} from "web-ng2/delt/komponenter/entitetikon/entitetikon";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";

const debug = initDebugger('BeslutningListevisningKompaktKomponent');

/**
 * Kompakt listevisning av beslutning, for utlisting i feks. sak
 */
@Component({
    selector: 'beslutning-listevisning-kompakt',
    templateUrl: 'beslutning-listevisning-kompakt.html',
    styleUrl: 'beslutning-listevisning-kompakt.scss',
    host: {
        '[id]': '"besl-"+this.beslutning().id',
        'data-testid': 'beslutning'
    },
    imports: [
        HtmlFormatertKomponent,
        MatIcon,
        BekreftKlikkDirektiv,
        MatIconButton,
        MatMenu,
        TranslateModule,
        MatMenuTrigger,
        MatMenuItem,
        OppgavelisteKompaktKomponent,
        MatDivider,
        ListeLenkerelasjonerKomponent,
        EntitetikonKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeslutningListevisningKompaktKomponent {

    /** css selector uttrykk som kan benyttes for å lokalisere elmenet som kan benyttes i 'scroll til' uttrykk.
     * Merk at vi ikke kan benytte #id direkte da beslutning-listevisning-kompakt elementet ikke er synlig (har display:contents) */
    static scrollTilElementCssUttrykk(beslutningId: BeslutningId): string {
        return `#besl-${beslutningId} > .detaljer > .tittel`;
    }

    /** tittel felt - brukt for å scrolle til ved fokus */
    tittelFelt = viewChild.required<ElementRef<HTMLElement>>('tittelFelt');

    beslutning = input.required<BeslutningModell>();

    /** Beslutning migrert fra sp2 vil ikke ha tittel, kun beskrivelse */
    harTittel = computed(() => !erMigrertBeslutning(this.beslutning()));
    harBeskrivelse = computed(() => erIkkeTomStreng(beslutningBeskrivelseEllerForslag(this.beslutning())));
    tittel = computed(() => nullsikkerBeslutningTittelEllerForslag(this.beslutning()));
    beskrivelse = computed(() => beslutningBeskrivelseEllerForslag(this.beslutning()));
    redigeringMulig = computed(() => kanBeslutningRedigeres(this.beslutning(), this.kontekst.autentisertBruker()));
    erFavoritt = computed(() => erFavorittHosKallendeBruker(this.beslutning(), this.kontekst.autentisertBruker()));
    oppgaver: Signal<OppgaveModell[]>;
    menyDeaktivert = this.navigasjonDeaktivering.navigasjonDeaktivertSomSignal

    constructor(private avhengigheter: BeslutningListevisningKompaktKomponentAvhengigheter,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjonDeaktivering: NavigasjonDeaktivering,
                private matDialog: MatDialog) {
        this.oppgaver = toSignal(toObservable(this.beslutning)
            .pipe(
              switchMap(beslutning => this.avhengigheter.oppgaver(beslutning.id)),
              takeUntilDestroyed()),
          {initialValue: []});
    }

    /** scroller til tittel-feltet og blinker .detaljer-raden */
    fokuser() {
        scrollDersomNødvendig(this.tittelFelt().nativeElement);
        this.tittelFelt().nativeElement.parentElement?.classList.add('ny')
    }

    nyOppgave() {
        OppgaveOpprettDialogKomponent.visOpprettUnderBeslutning(this.matDialog, this.beslutning());
    }

    async endreFavorittmarkering() {
        await this.avhengigheter.endreFavorittmarkering(this.beslutning());
    }

    rediger() {
        BeslutningRedigerDialogKomponent.vis(this.matDialog, this.beslutning());
    }

    async slett() {
        await this.avhengigheter.slett(this.beslutning().id);
    }

}

@Injectable()
export class BeslutningListevisningKompaktKomponentAvhengigheter {

    constructor(private beslutningDatatjeneste: BeslutningDatatjeneste, private oppgaveDatatjeneste: OppgaveDatatjeneste) {
    }

    oppgaver(id: BeslutningId): Observable<OppgaveModell[]> {
        return this.oppgaveDatatjeneste.finnTilknyttetBeslutningFraLokallager(id);
    }

    async endreFavorittmarkering(beslutning: BeslutningModell) {
        await this.beslutningDatatjeneste.endreFavorittmarkering(beslutning);
    }

    async slett(id: BeslutningId) {
        await this.beslutningDatatjeneste.slett(id);
    }

}
